import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Database, Zap, ToggleLeft, ToggleRight, Trash2, Loader2, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";

function StatCard({ icon: Icon, label, value, sub, tone = "default" }) {
  const toneCls = {
    default: "text-foreground",
    ok: "text-emerald-500",
    warn: "text-amber-500",
    danger: "text-rose-500",
  }[tone];
  return (
    <div className="ih-glass rounded-2xl p-5 ih-card-hover">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`font-display text-3xl font-medium ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function ConfigurazioneAI() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, l] = await Promise.all([
        api.get("/ai/health"),
        api.get("/audit-log?action=ai_call&limit=50").catch(() => ({ data: { items: [] } })),
      ]);
      setHealth(h.data);
      setLogs(l.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setActing(true);
    try {
      const r = await api.post("/ai/toggle", { enabled: !health.global_enabled });
      setHealth({ ...health, global_enabled: r.data.enabled });
      toast.success(r.data.enabled ? "AI abilitata globalmente" : "AI disabilitata globalmente");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setActing(false); }
  };

  const clearCache = async () => {
    if (!window.confirm("Svuotare la cache AI? Tutti i riepiloghi verranno rigenerati alla prossima richiesta.")) return;
    setActing(true);
    try {
      const r = await api.post("/ai/cache/clear");
      toast.success(`${r.data.deleted} voci cache eliminate`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setActing(false); }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="configurazione-ai-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Configurazione · AI Assistant
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Pannello AI</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitora utilizzo, cache e disponibilità dell'Assistente AI.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="ai-cfg-refresh">
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Aggiorna
        </Button>
      </div>

      {loading && !health ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="ih-glass rounded-2xl h-28 animate-pulse" />)}
        </div>
      ) : health && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Activity}
              label="Provider"
              value={health.model}
              sub={`Fallback: ${health.fallback_model}`}
              tone="ok"
            />
            <StatCard
              icon={Database}
              label="Cache attiva"
              value={health.cache_size}
              sub="TTL 24 ore"
            />
            <StatCard
              icon={Zap}
              label="Chiamate ultime 24h"
              value={health.calls_24h}
              sub={`Rate limit: ${health.rate_limit_per_hour}/ora`}
            />
            <StatCard
              icon={health.global_enabled ? ToggleRight : ToggleLeft}
              label="Stato globale"
              value={health.global_enabled ? "Attiva" : "Sospesa"}
              sub={health.ok ? "Chiave configurata" : "Chiave assente"}
              tone={health.global_enabled && health.ok ? "ok" : "danger"}
            />
          </div>

          {/* Azioni */}
          <div className="ih-glass rounded-2xl p-6">
            <h2 className="font-display text-lg tracking-tight mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-lavender-500" /> Azioni amministrative
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border/60 p-4 bg-background/40">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-sm">Disponibilità AI</div>
                    <p className="text-xs text-muted-foreground mt-1">Sospendi temporaneamente tutte le chiamate verso il provider LLM.</p>
                  </div>
                  <Button
                    onClick={toggle}
                    disabled={acting}
                    variant={health.global_enabled ? "outline" : "default"}
                    data-testid="ai-toggle-btn"
                  >
                    {health.global_enabled ? "Disabilita" : "Riabilita"}
                  </Button>
                </div>
              </div>
              <div className="rounded-xl border border-border/60 p-4 bg-background/40">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-sm">Cache risposte</div>
                    <p className="text-xs text-muted-foreground mt-1">Cancella i riepiloghi memorizzati. Saranno rigenerati on-demand.</p>
                  </div>
                  <Button
                    onClick={clearCache}
                    disabled={acting || health.cache_size === 0}
                    variant="outline"
                    data-testid="ai-clear-cache-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Svuota
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Ispezione log AI */}
          <div className="ih-glass rounded-2xl p-6">
            <h2 className="font-display text-lg tracking-tight mb-4 flex items-center gap-2">
              <Activity className="h-4 w-4" /> Ultime chiamate AI ({logs.length})
            </h2>
            {logs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna chiamata registrata di recente.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border/40">
                      <th className="text-left py-2 px-2">Quando</th>
                      <th className="text-left py-2 px-2">Utente</th>
                      <th className="text-left py-2 px-2">Endpoint</th>
                      <th className="text-left py-2 px-2">Modello</th>
                      <th className="text-left py-2 px-2">Cache</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.slice(0, 30).map((l) => (
                      <tr key={l.id} className="border-b border-border/20 hover:bg-accent/20" data-testid={`ai-log-${l.id}`}>
                        <td className="py-2 px-2 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(l.timestamp).toLocaleString("it-IT")}
                        </td>
                        <td className="py-2 px-2 text-xs">{l.user?.email || l.user_id?.slice(0, 8)}</td>
                        <td className="py-2 px-2 text-xs font-mono">{l.details?.endpoint || l.entity_id}</td>
                        <td className="py-2 px-2 text-xs">{l.details?.model || "—"}</td>
                        <td className="py-2 px-2 text-xs">
                          {l.details?.cached ? (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase bg-lavender-100 text-lavender-700 dark:bg-lavender-900/40 dark:text-lavender-300">cache</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">live</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
